import TableEditViewState from '../store/schema/TableEditViewState';
import setTableEditMenuViewState from '../actions/setTableEditMenuViewState';
import { Editor } from 'roosterjs-editor-core';
import { EditorPluginEx, PluginUtils, lazySetTableHeaderRow } from 'owa-editor';
import { TableOperation, TableFormat } from 'roosterjs-editor-types';

const TABLE_CELL_TAG_NAME = 'TD';
const TABLE_HEADER_TAG_NAME = 'TH';

function createTableFormat(
    bgColorEven?: string,
    bgColorOdd?: string,
    topBorder?: string,
    bottomBorder?: string,
    verticalBorder?: string
): TableFormat {
    return {
        bgColorEven: bgColorEven,
        bgColorOdd: bgColorOdd,
        topBorderColor: topBorder,
        bottomBorderColor: bottomBorder,
        verticalBorderColor: verticalBorder,
    };
}

const TABLE_FORMAT_MAP: { [name: string]: TableFormat } = {
    Default: createTableFormat('#FFF', '#FFF', '#ABABAB', '#ABABAB', '#ABABAB'),
    LightLines: createTableFormat('#FFF', '#FFF', null, '#92C0E0'),
    TwoTones: createTableFormat('#C0E4FF', '#FFF'),
    LightBands: createTableFormat('#D8D8D8', '#FFF'),
    Grid: createTableFormat('#D8D8D8', '#FFF', '#ABABAB', '#ABABAB', '#ABABAB'),
    Clear: createTableFormat('#FFF', '#FFF'),
};

export default class TableEditPlugin implements EditorPluginEx {
    private editor: Editor;
    private pluginUtils: PluginUtils;
    private range: Range;
    private onContextMenuDisposer: () => void;
    private invokedTableCell: HTMLTableCellElement;

    constructor(private viewState: TableEditViewState) {}

    getName() {
        return 'TableEdit';
    }

    initialize(editor: Editor) {
        this.editor = editor;
        this.onContextMenuDisposer = this.editor.addDomEventHandler(
            'contextmenu',
            this.onContextMenu
        );
    }

    dispose() {
        if (this.onContextMenuDisposer) {
            this.onContextMenuDisposer();
            this.onContextMenuDisposer = null;
        }
        this.editor = null;
        this.invokedTableCell = null;
    }

    setPluginUtils(pluginUtils: PluginUtils) {
        this.pluginUtils = pluginUtils;
    }

    editTable(operation: TableOperation) {
        if (this.range && this.editor) {
            this.editor.select(this.range);
            this.pluginUtils.editTable(this.editor, operation);
            this.range = null;
        }
    }

    formatTable(formatName: string) {
        let tableFormat = TABLE_FORMAT_MAP[formatName];
        if (tableFormat && this.range && this.editor) {
            this.editor.select(this.range);
            this.pluginUtils.formatTable(this.editor, tableFormat);
            this.range = null;
        }
    }

    async setTableHeaders(hasHeaders: boolean) {
        if (this.range && this.editor) {
            const setTableHeaderRow = await lazySetTableHeaderRow.import();
            setTableHeaderRow(this.editor, this.invokedTableCell, hasHeaders);
        }

        this.invokedTableCell = null;
    }

    private onContextMenu = (e: MouseEvent) => {
        let td = this.editor.getElementAtCursor(`${TABLE_CELL_TAG_NAME},${TABLE_HEADER_TAG_NAME}`, <
            HTMLElement
        >(e.srcElement || e.target)) as HTMLTableCellElement;

        if (td) {
            this.range = this.editor.getSelectionRange();
            this.invokedTableCell = td;
            const vTable = this.pluginUtils.createVTable(this.invokedTableCell);
            let hasHeaders = true;
            for (let idx = 0; idx < vTable.cells[0].length; idx++) {
                const vCellTD = vTable.cells[0][idx].td;
                // Virtual cells' td can be null due to cell merging. Make sure it exists before checking tagName.
                if (vCellTD && vCellTD.tagName != TABLE_HEADER_TAG_NAME) {
                    hasHeaders = false;
                    break;
                }
            }
            setTableEditMenuViewState(
                this.viewState,
                {
                    x: e.pageX,
                    y: e.pageY,
                },
                hasHeaders
            );
            e.preventDefault();
        }
    };
}
